/**
 * Utility functions for parsing quantities and units from shopping list and recipe ingredients.
 */

export interface ParsedShoppingAmount {
  amount: string
  unit: string
}

/**
 * Parses amounts in shopping list items (e.g., "2 kg", "500g", "3 x", "Flasche").
 * Strips lone 'x' or 'X' unit indicators.
 */
const AMOUNT_UNIT_REGEX = /^([0-9,.]+)\s*(.*)$/
const RANGE_AMOUNT_REGEX = /^([\d.,/]+(?:\s+[\d.,/]+)?)\s*[-–—]\s*([\d.,/]+(?:\s+[\d.,/]+)?)(.*)$/
const SINGLE_AMOUNT_REGEX = /^([\d.,/]+(?:\s+[\d.,/]+)?)\s*(.*)$/

export function parseAmountAndUnit(raw: string): ParsedShoppingAmount {
  const trimmed = (raw || '').trim()
  if (!trimmed) {
    return { amount: '', unit: '' }
  }

  // Match starting numbers (including decimals/commas) followed by any trailing text
  const match = trimmed.match(AMOUNT_UNIT_REGEX)
  if (match) {
    const amount = match[1]
    const unit = match[2].trim()
    if (unit === 'x' || unit === 'X') {
      return { amount, unit: '' }
    }
    return { amount, unit }
  }

  // If no leading number, treat the whole string as the unit (e.g. "Flasche")
  return { amount: '', unit: trimmed }
}

export interface ParsedIngredientAmount {
  quantity: string
  maxQuantity: string
  isRange: boolean
  unit: string
}

/**
 * Parses recipe ingredient amount strings into structured parts.
 * Handles single amounts ("500 g", "1 1/2 EL") and ranges ("10 - 12 g", "1-2 TL").
 */
export function parseIngredientAmount(amount: string): ParsedIngredientAmount {
  if (!amount) {
    return { quantity: '', maxQuantity: '', isRange: false, unit: '' }
  }
  const trimmed = amount.trim()

  // Match range: e.g. "10 - 12 g", "10-12", "1 1/2 - 2 EL"
  const rangeMatch = trimmed.match(RANGE_AMOUNT_REGEX)
  if (rangeMatch) {
    return {
      quantity: rangeMatch[1].trim(),
      maxQuantity: rangeMatch[2].trim(),
      isRange: true,
      unit: rangeMatch[3].trim()
    }
  }

  // Match single: e.g. "500 g", "1 1/2 EL"
  const match = trimmed.match(SINGLE_AMOUNT_REGEX)
  if (match) {
    return {
      quantity: match[1].trim(),
      maxQuantity: '',
      isRange: false,
      unit: match[2].trim()
    }
  }

  return { quantity: '', maxQuantity: '', isRange: false, unit: trimmed }
}

/**
 * Parses recipe ingredient amount and returns quantity as a single string (preserving ranges) and unit.
 */
export function parseRecipeAmount(amount: string): { quantity: string; unit: string } {
  if (!amount) return { quantity: '', unit: '' }
  const parsed = parseIngredientAmount(amount)
  return {
    quantity: parsed.isRange ? `${parsed.quantity} - ${parsed.maxQuantity}` : parsed.quantity,
    unit: parsed.unit
  }
}
